import type { ReviewComment } from "@/components/ai/review/types";
import { useReviewComments } from "@/components/ai/review/use-review-comments";
import { EnvironmentLifetimeExceededModal } from "@/components/environments/EnvironmentLifetimeExceededModal";
import { useLifetimeEnforcement } from "@/hooks/use-lifetime-enforcement";
import { Banner } from "@/components/flexkit/Banner";
import { cn } from "@/components/podkit/lib/cn";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Text } from "@/components/podkit/typography/Text";
import { useEnvironmentConversation, getAgentExecution } from "@/hooks/use-environment-conversation";
import { useOptimisticPendingMessages } from "@/hooks/use-pending-messages";
import { useStreamConversation } from "@/hooks/use-stream-conversation";
import { useEnvironment, useStartEnvironment } from "@/queries/environment-queries";
import { FileChangeList, type FileChangeListRef } from "@/routes/environments/code-changes/FileChangeList";
import { WorkerPoolContextProvider } from "@pierre/diffs/react";
import { getFiletypeFromFileName, type SupportedLanguages } from "@pierre/diffs";
import DiffsWorker from "@pierre/diffs/worker/worker.js?worker";
import { CodeChangesPanelActionBar } from "@/routes/environments/details/CodeChangesPanelActionBar";
import { effectiveState, showStart } from "@/routes/environments/phase";
import { formatError } from "@/utils/errors";

import { EnvironmentGitStatus, EnvironmentPhase } from "gitpod-next-api/gitpod/v1/environment_pb";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";

export type CodeChangesPanelRef = {
    scrollToFile: (filePath: string) => void;
    openFile: (filePath: string) => void;
    openFileWithPreview: (filePath: string) => void;
};

export type CodeChangesPanelProps = {
    environmentId: string;
    enableReviewComments?: boolean;
    /** File path queued for preview — processed on mount, then cleared via callback */
    pendingSpecPreview?: string | null;
    onPendingSpecPreviewConsumed?: () => void;
};

export const CodeChangesPanel = forwardRef<CodeChangesPanelRef, CodeChangesPanelProps>(
    ({ environmentId, enableReviewComments = false, pendingSpecPreview, onPendingSpecPreviewConsumed }, ref) => {
        const pinnedFileSuffix = "spec.md";
        const { data: environment } = useEnvironment(environmentId);
        const startEnvironment = useStartEnvironment();
        const isStarting = startEnvironment.isPending;
        const { toast } = useToast();
        const { enforcementEnabled, handleStartError, modalProps } = useLifetimeEnforcement();
        const [openedFiles, setOpenedFiles] = useState<string[]>([]);
        const fileChangeListRef = useRef<FileChangeListRef>(null);
        const stickyHeaderRef = useRef<HTMLDivElement>(null);
        const sentinelRef = useRef<HTMLDivElement>(null);
        const [isHeaderStuck, setIsHeaderStuck] = useState(false);

        useEffect(() => {
            const sentinel = sentinelRef.current;
            if (!sentinel) return;

            const scrollContainer = sentinel.closest("[data-radix-scroll-area-viewport]");

            const observer = new IntersectionObserver(
                ([entry]) => {
                    setIsHeaderStuck(!entry.isIntersecting);
                },
                {
                    root: scrollContainer,
                    threshold: 0,
                },
            );

            observer.observe(sentinel);

            return () => {
                observer.disconnect();
            };
        }, []);

        // Collapse all expanded file items when the environment leaves the running state.
        // We track the previous phase so that expanding a file while stopped (which
        // triggers a start and transitions through STARTING) doesn't immediately collapse.
        const envPhase = environment ? effectiveState(environment).state : undefined;
        const prevPhaseRef = useRef(envPhase);
        useEffect(() => {
            const prev = prevPhaseRef.current;
            prevPhaseRef.current = envPhase;
            if (prev === EnvironmentPhase.RUNNING && envPhase !== undefined && envPhase !== EnvironmentPhase.RUNNING) {
                setOpenedFiles([]);
            }
        }, [envPhase]);

        const { environmentConversation } = useEnvironmentConversation(environmentId);
        const agentExecution = getAgentExecution(environmentConversation);

        const { pendingMessages } = useOptimisticPendingMessages();
        const { messages } = useStreamConversation(
            agentExecution?.id,
            agentExecution?.status?.conversationUrl,
            pendingMessages,
        );

        const {
            comments,
            commentsByFile,
            addComment,
            updateComment,
            removeComment,
            clearAllComments,
            isAnnotationDismissed,
            setCommentThumbsUp,
            setCommentThumbsDown,
        } = useReviewComments(environmentId);

        const processedAnnotationsRef = useRef<Set<string>>(new Set());
        const commentsRef = useRef(comments);
        commentsRef.current = comments;
        const openedFilesRef = useRef(openedFiles);
        openedFilesRef.current = openedFiles;

        useEffect(() => {
            console.log("Processing agent annotations effect triggered");
            if (!enableReviewComments || !messages || !environmentId) return;
            console.log("Processing messages:", messages.length);

            const filesToOpen: string[] = [];

            const existingAnnotationIds = new Set(
                commentsRef.current.filter((c) => c.annotationId).map((c) => c.annotationId),
            );

            for (const message of messages) {
                if (message.type !== "agent") continue;
                for (const fragment of message.fragments) {
                    if (fragment.type === "codeAnnotation") {
                        const annotation = fragment;

                        if (processedAnnotationsRef.current.has(annotation.id)) continue;

                        if (isAnnotationDismissed(annotation.id)) {
                            console.log("dismissed by user, skipping");
                            processedAnnotationsRef.current.add(annotation.id);
                            continue;
                        }

                        if (existingAnnotationIds.has(annotation.id)) {
                            console.log("processed already, skipping");
                            processedAnnotationsRef.current.add(annotation.id);
                            continue;
                        }

                        processedAnnotationsRef.current.add(annotation.id);
                        addComment(
                            annotation.filePath,
                            annotation.startLine,
                            annotation.endLine,
                            annotation.description,
                            "agent",
                            annotation.id,
                        );

                        if (
                            !filesToOpen.includes(annotation.filePath) &&
                            !openedFilesRef.current.includes(annotation.filePath)
                        ) {
                            filesToOpen.push(annotation.filePath);
                        }
                    }
                }
            }

            if (filesToOpen.length > 0) {
                setOpenedFiles((prev) => [...prev, ...filesToOpen]);
            }
        }, [enableReviewComments, messages, environmentId, addComment, isAnnotationDismissed]);

        const gitStatus = environment?.status?.content?.git;
        const hasChanges = Boolean(gitStatus?.changedFiles.length);
        const shouldShowStartEnv = environment ? showStart(environment) : false;

        const langs = useMemo(() => {
            const seen = new Set<SupportedLanguages>();
            for (const file of gitStatus?.changedFiles ?? []) {
                const lang = getFiletypeFromFileName(file.path);
                if (lang !== "text") {
                    seen.add(lang);
                }
            }
            return Array.from(seen);
        }, [gitStatus?.changedFiles]);

        const tryStartEnvironment = useCallback(async () => {
            if (!environment) return;
            try {
                await startEnvironment.mutateAsync({
                    environmentId: environment.id,
                    skipPolicyCheck: enforcementEnabled,
                });
            } catch (error) {
                if (handleStartError(error)) {
                    return;
                }
                toast({
                    title: "Failed to start environment",
                    description: formatError(error),
                });
            }
        }, [environment, startEnvironment, enforcementEnabled, handleStartError, toast]);

        const handleOpenedFilesChange = useCallback(
            (files: string[]) => {
                if (shouldShowStartEnv && environment) {
                    void tryStartEnvironment();
                }
                setOpenedFiles(files);
            },
            [shouldShowStartEnv, environment, tryStartEnvironment],
        );

        const changedFilePaths = useMemo(
            () => new Set(gitStatus?.changedFiles.map((f) => f.path) ?? []),
            [gitStatus?.changedFiles],
        );
        const commentsInChangeList = useMemo(
            () => comments.filter((c) => changedFilePaths.has(c.filePath)),
            [comments, changedFilePaths],
        );

        useImperativeHandle(
            ref,
            () => ({
                scrollToFile: (filePath: string) => {
                    fileChangeListRef.current?.scrollToFile(filePath);
                },
                openFile: (filePath: string) => {
                    if (!openedFiles.includes(filePath)) {
                        setOpenedFiles((prev) => [...prev, filePath]);
                    }
                    fileChangeListRef.current?.scrollToFile(filePath);
                },
                openFileWithPreview: (filePath: string) => {
                    fileChangeListRef.current?.setFilePreviewMode(filePath);
                    if (!openedFiles.includes(filePath)) {
                        setOpenedFiles((prev) => [...prev, filePath]);
                    }
                    fileChangeListRef.current?.scrollToFile(filePath);
                },
            }),
            [openedFiles],
        );

        useEffect(() => {
            if (!pendingSpecPreview) return;
            if (!fileChangeListRef.current) return;

            // Resolve absolute path from fileModification to relative git status path
            const matchingFile = gitStatus?.changedFiles.find(
                (f) => pendingSpecPreview.endsWith(f.path) || f.path.endsWith(pendingSpecPreview),
            );
            const filePath = matchingFile?.path ?? pendingSpecPreview;

            fileChangeListRef.current.setFilePreviewMode(filePath);
            if (!openedFilesRef.current.includes(filePath)) {
                setOpenedFiles((prev) => [...prev, filePath]);
            }
            requestAnimationFrame(() => {
                fileChangeListRef.current?.scrollToFile(filePath);
            });

            onPendingSpecPreviewConsumed?.();
        }, [pendingSpecPreview, onPendingSpecPreviewConsumed, hasChanges, gitStatus?.changedFiles]);

        const handleStartEnvironment = () => {
            void tryStartEnvironment();
        };

        const handleAddComment = useCallback(
            (
                filePath: string,
                startLine: number,
                endLine: number,
                content: string,
                side?: "deletions" | "additions",
                contentHash?: string,
            ) => {
                addComment(filePath, startLine, endLine, content, "user", undefined, side, contentHash);
            },
            [addComment],
        );

        const handleUpdateComment = useCallback(
            (reviewComment: ReviewComment, newContent: string) => {
                updateComment(reviewComment.id, newContent);
            },
            [updateComment],
        );

        const handleDeleteComment = useCallback(
            (reviewComment: ReviewComment) => {
                removeComment(reviewComment.id);
            },
            [removeComment],
        );

        const handleSetCommentThumbsUp = useCallback(
            (reviewComment: ReviewComment, thumbsUp: boolean) => {
                setCommentThumbsUp(reviewComment.id, thumbsUp);
            },
            [setCommentThumbsUp],
        );

        const handleSetCommentThumbsDown = useCallback(
            (reviewComment: ReviewComment, thumbsDown: boolean) => {
                setCommentThumbsDown(reviewComment.id, thumbsDown);
            },
            [setCommentThumbsDown],
        );

        if (!environment) {
            return null;
        }

        // WorkerPoolContextProvider uses a module-level singleton — the pool is created
        // once and shared with EnvironmentCodeChanges. Only the first mount's options take effect.
        return (
            <WorkerPoolContextProvider
                poolOptions={{ workerFactory: () => new DiffsWorker() }}
                highlighterOptions={{ langs }}
            >
                <div>
                    {/* Sentinel for detecting when sticky header becomes stuck */}
                    {enableReviewComments && <div ref={sentinelRef} className="h-0 w-full" aria-hidden="true" />}
                    {/* Sticky header: negative top/margins offset the ScrollArea padding (p-4 md:p-6) in EnvironmentDetailsLayout.tsx so header sticks at viewport edge */}
                    <div
                        ref={enableReviewComments ? stickyHeaderRef : undefined}
                        className={cn(
                            "flex items-center justify-between py-2.5 @container",
                            enableReviewComments &&
                                "sticky -top-4 z-10 -mx-4 w-[calc(100%+2rem)] bg-surface-primary px-4 md:-top-6 md:-mx-6 md:w-[calc(100%+3rem)] md:px-6",
                            enableReviewComments && isHeaderStuck && "border-b border-border-subtle",
                        )}
                    >
                        <span className="mr-2 select-none text-start text-md font-medium">Code Changes</span>
                        {enableReviewComments && hasChanges && (
                            <CodeChangesPanelActionBar
                                environmentId={environmentId}
                                agentExecution={agentExecution}
                                commentsInChangeList={commentsInChangeList}
                                clearAllComments={clearAllComments}
                                changedFiles={gitStatus?.changedFiles ?? []}
                            />
                        )}
                    </div>
                    {shouldShowStartEnv && hasChanges && (
                        <Banner
                            className="mb-2"
                            variant="info"
                            text="Start the environment to view code changes."
                            action={{
                                text: "Start environment",
                                onClick: handleStartEnvironment,
                                loading: isStarting,
                                "data-tracking-id": "start-environment-code-changes-panel",
                            }}
                        />
                    )}
                    {hasChanges ? (
                        <FileChangeList
                            ref={fileChangeListRef}
                            environment={environment}
                            gitStatus={new EnvironmentGitStatus(gitStatus)}
                            openedFiles={openedFiles}
                            onOpenedFilesChange={handleOpenedFilesChange}
                            commentsByFile={enableReviewComments ? commentsByFile : undefined}
                            onAddComment={enableReviewComments ? handleAddComment : undefined}
                            onUpdateComment={enableReviewComments ? handleUpdateComment : undefined}
                            onDeleteComment={enableReviewComments ? handleDeleteComment : undefined}
                            onSetCommentThumbsUp={enableReviewComments ? handleSetCommentThumbsUp : undefined}
                            onSetCommentThumbsDown={enableReviewComments ? handleSetCommentThumbsDown : undefined}
                            pinnedFileSuffix={pinnedFileSuffix}
                        />
                    ) : (
                        <Text className="text-sm text-content-secondary">No uncommitted changes</Text>
                    )}
                </div>
                <EnvironmentLifetimeExceededModal environment={environment} {...modalProps} />
            </WorkerPoolContextProvider>
        );
    },
);
CodeChangesPanel.displayName = "CodeChangesPanel";
